import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { useAlertsStore } from '../store/alerts';
import { useAuth } from '../store/auth';
import { usePollMultiplier } from '../hooks/usePollMultiplier';
import { formatCurrency } from '../lib/utils';
import { CountryFlag } from './ui/CountryFlag';
import { X } from 'lucide-react';
// Imported as Vite asset → filename gets a content hash (notify-abc123.mp3)
// so browsers + CDN automatically cache-bust whenever the file changes.
// Previously this was served from public/ at a fixed URL, which meant users
// kept hearing the old sound even after we replaced the file.
import notifyMp3 from '../assets/notify.mp3';

/**
 * Global conversion-alert system.
 *
 * Mounted once at App level (above route outlet). Polls /api/v1/conversions
 * every 5s; on detecting conversions newer than `lastSeenConversionId`:
 *   1. Plays notification sound (default synth or user-uploaded custom)
 *   2. Pushes a toast with sub_id + revenue (auto-dismiss 6s)
 *   3. Flashes the sub_id everywhere on screen via global store
 *
 * Works on EVERY page (not just /conversions) because mounted globally.
 */

interface ConversionLite {
  id: number;
  subId: string | null;
  revenue: number;
  createdAt: number;
  status: string;
  networkOfferName: string | null;
  country: string | null;
}

interface Toast {
  id: number;
  subId: string;
  revenue: number;
  status: string;
  offerName: string | null;
  country: string | null;
  createdAt: number;
}

export function ConversionAlertProvider() {
  const { user } = useAuth();
  const enabled = useAlertsStore((s) => s.enabled);
  const lastSeenId = useAlertsStore((s) => s.lastSeenConversionId);
  const setLastSeenId = useAlertsStore((s) => s.setLastSeenConversionId);
  const flashSubId = useAlertsStore((s) => s.flashSubId);
  const audioPref = useAlertsStore((s) => s.audioPref);
  const audioBlob = useAlertsStore((s) => s.audioBlob);
  const audioVolume = useAlertsStore((s) => s.audioVolume);
  // Slow alert polling for publisher role — they don't need sub-10s
  // notification of their own conversions (they'll see them at next refresh).
  const pollMult = usePollMultiplier();

  // Smart skip: when user is already on /conversions page, that page polls
  // the same endpoint at the same cadence — no need for the global alert
  // poller to duplicate the work. The page renders new entries directly
  // in the table; toasts on /conversions add no information value.
  // We detect via useLocation rather than passing a prop down because
  // the provider is mounted at App level above the route outlet.
  const location = useLocation();
  const onConversionsPage = location.pathname === '/conversions';
  const shouldPoll = enabled && !onConversionsPage;

  const [toasts, setToasts] = useState<Toast[]>([]);
  // Toast auto-dismiss timers — tracked so we can clear them on unmount
  // (otherwise setTimeout fires `setToasts` on unmounted component → React warning).
  const toastTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  // Bootstrap: on first poll, just record the latest id without flashing
  // (don't spam alerts for everything that happened before user opened tab)
  const bootstrappedRef = useRef(false);

  // Poll /api/v1/conversions for new entries.
  // - limit raised to 200 so heavy-traffic windows don't silently cut off conversions.
  // - since_id passed after bootstrap so the API can filter server-side to rows after
  //   the high-water mark; no since_days race after >24h offline.
  // BUGFIX: queryKey includes lastSeenId so TanStack Query treats each window as
  // a distinct request — without this, the cache returned stale results when
  // the high-water mark advanced (queryFn closure captured old since_id).
  const query = useQuery({
    queryKey: ['conversion-alerts', bootstrappedRef.current ? lastSeenId : 'boot'],
    queryFn: () =>
      apiClient.get<ConversionLite[]>('/api/v1/conversions', {
        since_days: 1,
        limit: 200,
        // Once bootstrapped, send since_id so the server returns only fresh rows.
        // Before bootstrap (lastSeenId=0 and not yet bootstrapped), omit it so we
        // get the most recent page to determine the starting high-water mark.
        ...(bootstrappedRef.current && lastSeenId > 0 ? { since_id: lastSeenId } : {}),
      }),
    // 10s interval × pollMult (1 admin / 2 publisher). Tab-background pause
    // (set globally via refetchIntervalInBackground: false) handles
    // multi-tab amplification. Smart-skip on /conversions page handles
    // duplicate polling with that page's own query.
    refetchInterval: shouldPoll ? 10_000 * pollMult : false,
    enabled: !!user && shouldPoll,
    retry: false,
  });

  // Cleanup all timers on unmount (e.g. logout mid-toast).
  useEffect(() => {
    return () => {
      for (const t of toastTimersRef.current.values()) clearTimeout(t);
      toastTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const data = query.data;
    if (!data) return;

    // Find the newest id in response (empty array is valid after bootstrap)
    const maxId = data.length > 0 ? Math.max(...data.map((c) => c.id)) : 0;

    // Bootstrap logic:
    //   - If we have a persisted lastSeenId > 0, treat this as already bootstrapped:
    //     skip silent-bootstrap and alert on everything newer (handles HMR resets
    //     and cross-tab reloads correctly — bootstrappedRef resets but lastSeenId
    //     is persisted, so we don't re-alert history or miss the HMR window).
    //   - If lastSeenId is 0 (first ever open), do one silent bootstrap pass.
    if (!bootstrappedRef.current) {
      bootstrappedRef.current = true;
      if (lastSeenId > 0) {
        // Persisted high-water mark exists — already bootstrapped from a previous
        // session. Any conversions in this response with id > lastSeenId are fresh.
        // Fall through to the normal alert path below.
      } else {
        // True first visit — record current max as baseline, fire no alerts.
        if (maxId > 0) setLastSeenId(maxId);
        return;
      }
    }

    // New conversions = those with id > lastSeenId
    const fresh = data.filter((c) => c.id > lastSeenId);
    if (fresh.length === 0) return;

    // Sort oldest-first so toasts show in chronological order
    fresh.sort((a, b) => a.id - b.id);

    // Cap toast stack at MAX_TOASTS — burst of 50 conversions in one poll
    // shouldn't spam the screen. We push all but only keep the most recent.
    const MAX_TOASTS = 5;
    for (const conv of fresh) {
      // Use sentinel that cannot collide with a real publisher slug. A
      // publisher literally named "unlinked" would otherwise share the flash
      // with null-sub_id conversions.
      const sub = conv.subId || '__unlinked__';
      // Target-lock effect — overlay falls back to center-screen if no DOM
      // target matches this sentinel (which is the desired UX).
      flashSubId(sub);
      // Toast (cap to MAX_TOASTS, drop oldest)
      setToasts((prev) => {
        const next = [
          ...prev,
          {
            id: conv.id,
            subId: sub,
            revenue: conv.revenue,
            status: conv.status,
            offerName: conv.networkOfferName,
            country: conv.country,
            createdAt: Date.now(),
          },
        ];
        if (next.length > MAX_TOASTS) {
          // Clear the timers for toasts we're about to drop so they don't
          // fire later and try to remove items that no longer exist.
          for (const drop of next.slice(0, next.length - MAX_TOASTS)) {
            const t = toastTimersRef.current.get(drop.id);
            if (t) {
              clearTimeout(t);
              toastTimersRef.current.delete(drop.id);
            }
          }
          return next.slice(-MAX_TOASTS);
        }
        return next;
      });
      // Auto-dismiss after 6s — track timer so we can cancel on unmount.
      const timer = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== conv.id));
        toastTimersRef.current.delete(conv.id);
      }, 6_000);
      toastTimersRef.current.set(conv.id, timer);
    }

    // Play notification sound — once per batch even if multiple convs arrived
    void playNotification(audioPref, audioBlob, audioVolume);

    // Update high-water mark
    setLastSeenId(maxId);
  }, [query.data, lastSeenId, audioPref, audioBlob, audioVolume, flashSubId, setLastSeenId]);

  return (
    <>
      {/* Toast stack — bottom-right, fixed position, above everything.
          Bigger size (max-w-md, larger fonts) per user feedback ("kurang
          besar"). Country flag added to the headline for at-a-glance geo.
          Stacks newest-on-top by reversing render order. */}
      <div className="fixed bottom-4 right-4 z-[200] flex flex-col-reverse gap-2 pointer-events-none max-w-md w-[22rem]">
        {toasts.map((t) => {
          // Format timestamp as HH:MM:SS in user's local zone — terminal logs
          // always have wall-clock time, much more useful than relative.
          const ts = new Date(t.createdAt).toLocaleTimeString('en-GB', { hour12: false });
          return (
            <div
              key={t.id}
              className="pointer-events-auto bg-bg-secondary/95 backdrop-blur-sm font-mono border-l-2 border-neon-cyan pl-4 pr-9 py-3 relative"
              style={{
                animation: 'toast-slide-up 0.18s ease-out forwards',
                // Very subtle outer outline so toast separates from page bg
                // without using the loud magenta+yellow halo from the old design.
                boxShadow: '0 0 0 1px rgba(0, 255, 225, 0.15), 0 8px 24px rgba(0, 0, 0, 0.6)',
              }}
            >
              {/* Headline row: [HH:MM:SS] CONV ▸ <subid> ··· +$x.xx
                  Slightly bigger than before (text-sm vs text-xs) for
                  better legibility — toasts are time-critical. */}
              <div className="flex items-center gap-1.5 whitespace-nowrap text-sm">
                <span className="text-text-tertiary text-[11px]">[{ts}]</span>
                <span className="text-neon-cyan font-bold tracking-wide">CONV</span>
                <span className="text-text-tertiary">▸</span>
                <span className="text-text-primary truncate font-semibold">{t.subId}</span>
                <span className="text-neon-yellow ml-auto pl-2 font-bold text-base">
                  +{formatCurrency(t.revenue)}
                </span>
              </div>
              {/* Metadata row: country flag + offer name. Country first
                  so geo is always at-a-glance even if offer name is long
                  and gets truncated. Falls back gracefully when either
                  field is missing — common for unlinked or older convs. */}
              {(t.country || t.offerName) && (
                <div className="flex items-center gap-2 mt-1.5 text-[11px] leading-tight text-text-tertiary">
                  <span className="text-neon-cyan/40">╰╴</span>
                  {t.country && (
                    <span className="inline-flex items-center gap-1 shrink-0">
                      <CountryFlag iso2={t.country} size={14} />
                      <span className="text-text-secondary font-mono">{t.country}</span>
                    </span>
                  )}
                  {t.offerName && (
                    <span className="truncate">
                      {t.country && <span className="text-text-tertiary mx-1">·</span>}
                      {t.offerName}
                    </span>
                  )}
                </div>
              )}
              {/* Close button — minimal, top-right */}
              <button
                onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                className="absolute top-2 right-2 text-text-tertiary hover:text-neon-cyan transition-colors"
                aria-label="Dismiss notification"
              >
                <X size={13} />
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─── Audio playback ──────────────────────────────────────────────────────

// Default sound — imported as Vite asset (see top of file). Vite emits the
// file with a content hash, so any swap of the source mp3 forces a fresh URL
// and bypasses both browser and CDN cache automatically.
const DEFAULT_AUDIO_URL = notifyMp3;
let defaultAudioEl: HTMLAudioElement | null = null;
let customAudioEl: HTMLAudioElement | null = null;
let customAudioBlob: string | null = null;

/**
 * Play notification: either user-uploaded blob or bundled default MP3.
 *
 * Browsers block audio playback until user has interacted with page at least
 * once (autoplay policy). We catch the rejection silently — if user hasn't
 * clicked anywhere yet, no sound plays. After first interaction, all subsequent
 * plays work.
 *
 * Reuses cached <audio> elements to avoid re-fetching/decoding the MP3 every
 * conversion. Custom blob URL gets recreated only when blob changes.
 */
async function playNotification(
  pref: 'default' | 'custom',
  blob: string | null,
  volume: number,
): Promise<void> {
  try {
    let audio: HTMLAudioElement;
    if (pref === 'custom' && blob) {
      if (!customAudioEl || customAudioBlob !== blob) {
        customAudioEl = new Audio(blob);
        customAudioBlob = blob;
      }
      audio = customAudioEl;
    } else {
      if (!defaultAudioEl) {
        defaultAudioEl = new Audio(DEFAULT_AUDIO_URL);
      }
      audio = defaultAudioEl;
    }
    audio.volume = Math.max(0, Math.min(1, volume));
    // Rewind to start so rapid-fire conversions don't get cut off mid-play
    audio.currentTime = 0;
    await audio.play();
  } catch {
    // Browser blocked audio (no user interaction yet) — ignore silently
  }
}
